import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import type { Background, NormalSpreadBackgrounds } from '@/api/types/background'
import { checkpointBackgroundsService } from '@/api/services/checkpoint-backgrounds.service'
import { useBackgroundsQuery } from '@/api/hooks/use-backgrounds'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import type { SpreadBackgroundPlacements } from '@/lib/backgrounds/types/spread-background-placements'
import {
  applyBackgroundPlacement,
  clearBackgroundPlacement,
  resolveKeyboardBackgroundDropSlot,
} from '@/lib/backgrounds/background-placement'
import {
  DEFAULT_BACKGROUND_TINT,
  type SavedSpreadBackgrounds,
  buildBackgroundUpdateRequest,
  buildSavedBackgroundTintRequest,
  hasUnresolvedSavedBackgrounds,
  normalSpreadToSlots,
  readSavedSpreadBackgrounds,
  resolveSavedSpreadBackgrounds,
  savedBackgroundFromRequest,
  withSavedBackgroundTint,
} from '@/lib/backgrounds/saved-spread-backgrounds'
import {
  placedBackgroundIds,
  reconcileBackgroundPlacementOverrides,
} from '@/lib/backgrounds/background-placement-session'
import {
  type RestoreSpreadBackgrounds,
  type SpreadBackgroundsChange,
  isRetryableBackgroundWriteError,
  planSpreadBackgroundsRestore,
  savedSpreadBackgroundsEqual,
} from '@/lib/backgrounds/spread-backgrounds-history'
import { logger } from '@/utils/logger'
import {
  BackgroundDragGhost,
  backgroundDragGhostTransform,
} from '@/components/app/backgrounds/background-drag-ghost'

export type BackgroundPlacementCanvasApi = {
  isOverCanvas: (clientX: number, clientY: number) => boolean
  /** Resolves the drop slot straight from a client (viewport) point — the canvas side owns the
   *  client→canvas-point conversion and the page geometry, so this is the one hit-test entry
   *  point the context needs. */
  resolveDropSlot: (
    clientX: number,
    clientY: number,
    isDoublePageBackground: boolean
  ) => BackgroundDropSlot | null
  /** Which sides actually exist on the current spread — a cover (single-page) context only has
   *  one. Drives which page a keyboard placement targets. */
  availableSides: { left: boolean; right: boolean }
}

/**
 * The part of the context whose identity only changes when the Backgrounds panel opens or closes
 * (or the checkpoint finishes loading) — never during a drag, a hover-target change, or a tint
 * slider tick. Consumers that only need to START a drag or read the panel state (every tray
 * thumbnail, and `CenterCanvas`, which is expensive to re-render) should read this through
 * `useBackgroundPlacementActionsOptional`, so fast-changing drag state can't re-render them.
 */
export type BackgroundPlacementActions = Pick<
  BackgroundPlacementContextValue,
  | 'beginPointerDrag'
  | 'activateViaKeyboard'
  | 'cancelDrag'
  | 'registerCanvasApi'
  | 'isBackgroundsPanelOpen'
  | 'setBackgroundsPanelOpen'
  | 'updateBackgroundTint'
  | 'removeBackground'
>

type BackgroundPlacementContextValue = {
  beginPointerDrag: (
    background: Background,
    clientX: number,
    clientY: number,
    pointerId: number,
    thumbRect: DOMRect
  ) => void
  /** Enter/Space activation on a background thumbnail — no cursor position to hit-test with, so
   *  the target page is resolved automatically (`resolveKeyboardBackgroundDropSlot`) and the
   *  background is placed there, same as a pointer drop. No-op while a pointer drag is already in
   *  progress, or before the canvas has registered its API. */
  activateViaKeyboard: (background: Background) => void
  cancelDrag: () => void
  isDraggingBackground: boolean
  draggingBackgroundId: string | null
  /** The background being dragged — the canvas renders it as a live preview in `hoverSlot`. */
  draggingBackground: Background | null
  hoverSlot: BackgroundDropSlot | null
  registerCanvasApi: (api: BackgroundPlacementCanvasApi | null) => void
  /** The current spread's backgrounds, resolved to library `Background`s for rendering. Saved to
   *  the checkpoint (PUT /checkpoints/{spreadId}/backgrounds) and hydrated from the loaded
   *  checkpoint's `spreadSpecialItems.normalSpread`. */
  placedBackgrounds: SpreadBackgroundPlacements
  /** True while the right-hand Backgrounds panel is mounted — the on-canvas background options
   *  toolbars only show then. Set by `BackgroundsPanel` itself (see `setBackgroundsPanelOpen`). */
  isBackgroundsPanelOpen: boolean
  setBackgroundsPanelOpen: (open: boolean) => void
  /**
   * Changes a placed background's tint. Always applied locally at once (so the canvas previews it
   * live); only saved when `persist` is true — a slider drag previews on every tick and saves once
   * on release. A failed save rolls back to the tint from before the unsaved previews began.
   */
  updateBackgroundTint: (slot: BackgroundDropSlot, tint: number, persist: boolean) => void
  /** Clears one slot (DELETE /checkpoints/{spreadId}/backgrounds/{slot}). */
  removeBackground: (slot: BackgroundDropSlot) => void
}

/** Toast for an undo/redo of a background change that failed for good (its step is dropped). */
const BACKGROUND_UNDO_IMPOSSIBLE_MESSAGE = "This background change can't be undone."

/** Stable reference so a spread with nothing placed yet doesn't recompute `contextValue`'s
 *  `useMemo` on every render — a fresh `{}` literal would never be `===` to the last one. */
const EMPTY_SAVED: SavedSpreadBackgrounds = {}

const BackgroundPlacementContext = createContext<BackgroundPlacementContextValue | null>(null)
const BackgroundPlacementActionsContext = createContext<BackgroundPlacementActions | null>(null)

/** Everything, including live drag state — re-renders on every drag/hover/tint change. Use it
 *  only where that state is actually rendered (the canvas fills, drop target, options toolbars). */
export function useBackgroundPlacementOptional(): BackgroundPlacementContextValue | null {
  return useContext(BackgroundPlacementContext)
}

/** The stable subset — see `BackgroundPlacementActions`. */
export function useBackgroundPlacementActionsOptional(): BackgroundPlacementActions | null {
  return useContext(BackgroundPlacementActionsContext)
}

type DragPhase = 'idle' | 'dragging'

type BackgroundPlacementProviderProps = {
  children: React.ReactNode
  spreadId: string
  /** `useSpreadCheckpoint`'s `data.metadata` — carries `nsDocument.spreadSpecialItems`. */
  checkpointMetadata: unknown
  /** The backend rejects background writes (404) until the checkpoint cache is loaded. */
  isCheckpointLoaded: boolean
  /** Home's undo bridge: assigned the restore function an undo/redo step calls. */
  restoreSpreadBackgroundsRef?: React.MutableRefObject<RestoreSpreadBackgrounds | null>
  /** Home's exit bridge: assigned a function that waits for a spread's queued background writes,
   *  so the checkpoint isn't exited while one is still in flight. */
  flushSpreadBackgroundWritesRef?: React.MutableRefObject<
    ((spreadId: string) => Promise<void>) | null
  >
  /** Each committed place, replace, remove or tint, reported for the undo stack. Not called for
   *  history replays (`restoreSpreadBackgroundsRef`). Returns a function that discards the
   *  recorded entry again, called if the change's save fails and rolls back. */
  onSpreadBackgroundsChanged?: (change: SpreadBackgroundsChange) => (() => void) | undefined
}

export function BackgroundPlacementProvider({
  children,
  spreadId,
  checkpointMetadata,
  isCheckpointLoaded,
  restoreSpreadBackgroundsRef,
  flushSpreadBackgroundWritesRef,
  onSpreadBackgroundsChanged,
}: BackgroundPlacementProviderProps) {
  const canvasApiRef = useRef<BackgroundPlacementCanvasApi | null>(null)

  const [phase, setPhase] = useState<DragPhase>('idle')
  const [background, setBackground] = useState<Background | null>(null)
  const [hoverSlot, setHoverSlot] = useState<BackgroundDropSlot | null>(null)
  const [ghostClient, setGhostClient] = useState({ x: 0, y: 0 })
  const [thumbRect, setThumbRect] = useState<DOMRect | null>(null)
  // Saved (server-shape) refs per spread. A spread is hydrated from its checkpoint once per
  // session; after that this state is authoritative — the checkpoint query is cached for 30 min,
  // so re-reading it on a spread switch would clobber placements made since it was fetched.
  const [savedBySpread, setSavedBySpread] = useState<Record<string, SavedSpreadBackgrounds>>({})
  // Backgrounds dropped this session — resolves their refs without waiting on the library query.
  const [droppedById, setDroppedById] = useState<Record<string, Background>>({})
  // Per-spread write sequence: only the latest write's response may overwrite state, so a slow
  // earlier PUT can't resurrect a placement a later one replaced.
  const writeSeqRef = useRef<Record<string, number>>({})
  // Per-spread write queue: the tail of every write issued for that spread (see
  // `runBackgroundWrite`).
  const writeChainRef = useRef<Record<string, Promise<void>>>({})
  // Per-spread state to roll a tint back to if its save fails — captured when the first unsaved
  // preview of a gesture lands, cleared once that gesture's save is issued.
  const tintRollbackRef = useRef<Record<string, SavedSpreadBackgrounds>>({})
  const [isBackgroundsPanelOpen, setBackgroundsPanelOpen] = useState(false)

  const spreadIdRef = useRef(spreadId)
  const savedBySpreadRef = useRef(savedBySpread)

  spreadIdRef.current = spreadId
  savedBySpreadRef.current = savedBySpread

  // Read through a ref so the write callbacks — and the stable actions context — keep one identity.
  const onSpreadBackgroundsChangedRef = useRef(onSpreadBackgroundsChanged)

  onSpreadBackgroundsChangedRef.current = onSpreadBackgroundsChanged

  /** Reports a committed edit for undo; a no-op edit (same backgrounds and tints) is not one.
   *  Returns the discard for the recorded entry, to run if the edit's save rolls back. */
  const recordChange = useCallback(
    (
      targetSpreadId: string,
      before: SavedSpreadBackgrounds,
      after: SavedSpreadBackgrounds
    ): (() => void) | undefined => {
      if (savedSpreadBackgroundsEqual(before, after)) return undefined
      return onSpreadBackgroundsChangedRef.current?.({ spreadId: targetSpreadId, before, after })
    },
    []
  )

  useEffect(() => {
    if (!spreadId || !isCheckpointLoaded) return

    const fromCheckpoint = readSavedSpreadBackgrounds(checkpointMetadata)

    if (!fromCheckpoint) return

    setSavedBySpread((prev) => (spreadId in prev ? prev : { ...prev, [spreadId]: fromCheckpoint }))
  }, [spreadId, isCheckpointLoaded, checkpointMetadata])

  // Keep the tray's session "Placed" state in step with every spread's slots. One place for it,
  // because every write path (place, replace, remove, undo/redo, failed-save rollback, checkpoint
  // hydration) lands in `savedBySpread`; a reconcile with nothing changed is a no-op.
  useEffect(() => {
    for (const [targetSpreadId, slots] of Object.entries(savedBySpread)) {
      reconcileBackgroundPlacementOverrides(targetSpreadId, placedBackgroundIds(slots))
    }
  }, [savedBySpread])

  const saved = savedBySpread[spreadId] ?? EMPTY_SAVED

  const droppedMap = useMemo(() => new Map(Object.entries(droppedById)), [droppedById])
  const needsLibrary = hasUnresolvedSavedBackgrounds(saved, droppedMap)
  // Same query keys the panel's default filters use, so this usually hits the panel's cache.
  const { data: libraryBackgrounds } = useBackgroundsQuery('any', 'All backgrounds', {
    enabled: needsLibrary,
  })

  const backgroundsById = useMemo(() => {
    const map = new Map<string, Background>()

    for (const bg of libraryBackgrounds) map.set(bg.backgroundId, bg)
    for (const [id, bg] of droppedMap) map.set(id, bg)

    return map
  }, [libraryBackgrounds, droppedMap])

  const phaseRef = useRef<DragPhase>('idle')
  const backgroundRef = useRef<Background | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  // Latest pointer position, read when the frame runs — so the ghost and hit-test use where the
  // cursor is now, not where it was at the first move of the frame.
  const latestClientRef = useRef({ x: 0, y: 0 })
  const ghostElRef = useRef<HTMLDivElement | null>(null)

  const registerCanvasApi = useCallback((api: BackgroundPlacementCanvasApi | null) => {
    canvasApiRef.current = api
  }, [])

  const resetDrag = useCallback(() => {
    pointerIdRef.current = null
    setPhase('idle')
    phaseRef.current = 'idle'
    backgroundRef.current = null
    setBackground(null)
    setThumbRect(null)
    setHoverSlot(null)
  }, [])

  const cancelDrag = useCallback(() => {
    if (phaseRef.current === 'idle') return
    resetDrag()
  }, [resetDrag])

  const beginPointerDrag = useCallback(
    (
      targetBackground: Background,
      clientX: number,
      clientY: number,
      pointerId: number,
      rect: DOMRect
    ) => {
      if (phaseRef.current !== 'idle') return

      backgroundRef.current = targetBackground
      setBackground(targetBackground)
      setThumbRect(rect)
      // Only the starting position goes through React; every later move is written straight to
      // the ghost element in `updateFromClient`.
      latestClientRef.current = { x: clientX, y: clientY }
      setGhostClient({ x: clientX, y: clientY })
      setHoverSlot(null)
      phaseRef.current = 'dragging'
      setPhase('dragging')
      pointerIdRef.current = pointerId
    },
    []
  )

  const updateFromClient = useCallback((clientX: number, clientY: number) => {
    latestClientRef.current = { x: clientX, y: clientY }
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null

      const { x, y } = latestClientRef.current
      const api = canvasApiRef.current
      const isDouble = Boolean(backgroundRef.current?.doublePageFlag)
      // Hit-test (layout reads: elementFromPoint, getBoundingClientRect) BEFORE moving the
      // ghost, so the reads see last frame's already-computed layout instead of forcing a fresh
      // synchronous one right after a style write.
      const slot = api && api.isOverCanvas(x, y) ? api.resolveDropSlot(x, y, isDouble) : null

      if (ghostElRef.current) {
        ghostElRef.current.style.transform = backgroundDragGhostTransform(x, y)
      }

      // Same-value sets bail out, so this only re-renders when the target actually changes.
      setHoverSlot(slot)
    })
  }, [])

  /**
   * Applies `optimistic` locally, then runs `writes` in order — after every earlier write for the
   * same spread has settled. One queue per spread: each change is planned against the optimistic
   * state of the ones before it, so they must reach the server in that order too (an undo pressed
   * while its edit's PUT is in flight would otherwise race it). Only the latest write for a spread
   * may settle its state. On success the state settles to the server's full NormalSpread; on a
   * mid-way failure to the last write that did land, or — if none did — to `rollback`. When none
   * landed, `onRolledBack` runs (the change didn't happen, so its history entry is undone), even
   * if a newer write has since taken over the spread's state.
   */
  const runBackgroundWrite = useCallback(
    ({
      targetSpreadId,
      optimistic,
      rollback,
      writes,
      errorMessage,
      onRolledBack,
    }: {
      targetSpreadId: string
      optimistic: SavedSpreadBackgrounds
      rollback: SavedSpreadBackgrounds
      writes: Array<() => Promise<NormalSpreadBackgrounds>>
      /** The error toast — or a function choosing it from the error that stopped the write. */
      errorMessage: string | ((error: unknown) => string)
      onRolledBack?: (error: unknown) => void
    }) => {
      const seq = (writeSeqRef.current[targetSpreadId] ?? 0) + 1

      writeSeqRef.current[targetSpreadId] = seq
      savedBySpreadRef.current = { ...savedBySpreadRef.current, [targetSpreadId]: optimistic }
      setSavedBySpread((prev) => ({ ...prev, [targetSpreadId]: optimistic }))

      const run = async () => {
        let latest: SavedSpreadBackgrounds | null = null
        let failed = false
        let failure: unknown = null

        try {
          for (const write of writes) {
            latest = normalSpreadToSlots(await write())
          }
        } catch (error) {
          failed = true
          failure = error
          logger.error('Failed to save spread background', error)
          toast.error(typeof errorMessage === 'function' ? errorMessage(error) : errorMessage)
        }

        if (failed && latest === null) onRolledBack?.(failure)

        if (writeSeqRef.current[targetSpreadId] !== seq) return

        const settled = latest ?? rollback

        setSavedBySpread((prev) => ({ ...prev, [targetSpreadId]: settled }))
      }

      // An idle spread starts the write right away; otherwise it waits its turn. `run` never
      // rejects (it catches its own write errors), so the queue never stalls.
      const previous = writeChainRef.current[targetSpreadId]
      const queued: Promise<void> = (previous ? previous.then(run) : run()).then(() => {
        // Last in line: the spread is idle again.
        if (writeChainRef.current[targetSpreadId] === queued) {
          delete writeChainRef.current[targetSpreadId]
        }
      })

      writeChainRef.current[targetSpreadId] = queued
    },
    []
  )

  /** Resolves once every background write queued so far for `targetSpreadId` has settled — home
   *  waits on it before exiting that spread's checkpoint (spread switch, logout). */
  const flushSpreadBackgroundWrites = useCallback(
    (targetSpreadId: string): Promise<void> =>
      writeChainRef.current[targetSpreadId] ?? Promise.resolve(),
    []
  )

  /**
   * Places `placedBackground` into `slot` at full tint and saves it. Tint is adjusted afterwards
   * from the on-canvas options toolbar.
   */
  const placeBackground = useCallback(
    (placedBackground: Background, slot: BackgroundDropSlot) => {
      const targetSpreadId = spreadIdRef.current

      if (!targetSpreadId || !isCheckpointLoaded) {
        toast.error('The spread is still loading. Try placing the background again.')
        return
      }

      const request = buildBackgroundUpdateRequest(placedBackground, slot, DEFAULT_BACKGROUND_TINT)

      if (!request) {
        toast.error("This background can't be placed.")
        logger.warn('Background has no saveable colorId', placedBackground)
        return
      }

      setDroppedById((prev) =>
        prev[placedBackground.backgroundId] === placedBackground
          ? prev
          : { ...prev, [placedBackground.backgroundId]: placedBackground }
      )

      delete tintRollbackRef.current[targetSpreadId]

      const previous = savedBySpreadRef.current[targetSpreadId] ?? EMPTY_SAVED
      // Optimistic: apply locally with the same exclusivity rule the backend enforces.
      const optimistic = applyBackgroundPlacement(
        previous,
        slot,
        savedBackgroundFromRequest(request)
      )

      runBackgroundWrite({
        targetSpreadId,
        optimistic,
        rollback: previous,
        writes: [() => checkpointBackgroundsService.updateBackground(targetSpreadId, request)],
        errorMessage: "Couldn't save the background. Please try again.",
        onRolledBack: recordChange(targetSpreadId, previous, optimistic),
      })
    },
    [isCheckpointLoaded, recordChange, runBackgroundWrite]
  )

  // `hoverSlot` is read fresh from the ref rather than taken as a dependency: `finishDrop` is
  // registered once into the pointerup listener effect below, so a stale closure here would keep
  // acting on whatever `hoverSlot` was at mount, not the value from the drag that's actually
  // ending.
  const hoverSlotRef = useRef<BackgroundDropSlot | null>(null)

  hoverSlotRef.current = hoverSlot

  const finishDrop = useCallback(() => {
    const api = canvasApiRef.current
    const activeBackground = backgroundRef.current
    const slot = hoverSlotRef.current

    if (!api || !activeBackground || !slot) {
      resetDrag()
      return
    }

    resetDrag()
    placeBackground(activeBackground, slot)
  }, [placeBackground, resetDrag])

  const activateViaKeyboard = useCallback(
    (targetBackground: Background) => {
      if (phaseRef.current !== 'idle') return

      const api = canvasApiRef.current

      if (!api) return

      // Read through the ref rather than closing over `saved`, so this callback — and the stable
      // actions context it's part of — keeps one identity as placements change.
      const current = savedBySpreadRef.current[spreadIdRef.current] ?? EMPTY_SAVED
      const slot = resolveKeyboardBackgroundDropSlot(targetBackground, current, api.availableSides)

      if (!slot) return

      placeBackground(targetBackground, slot)
    },
    [placeBackground]
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (phaseRef.current === 'idle') return

      e.preventDefault()
      e.stopPropagation()
      cancelDrag()
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [cancelDrag])

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (phaseRef.current === 'dragging' && pointerIdRef.current === e.pointerId) {
        updateFromClient(e.clientX, e.clientY)
      }
    }

    const onPointerUp = (e: PointerEvent) => {
      if (phaseRef.current === 'dragging' && pointerIdRef.current === e.pointerId) {
        finishDrop()
      }
    }

    const onPointerCancel = (e: PointerEvent) => {
      if (phaseRef.current === 'dragging' && pointerIdRef.current === e.pointerId) {
        cancelDrag()
      }
    }

    window.addEventListener('pointermove', onPointerMove, true)
    window.addEventListener('pointerup', onPointerUp, true)
    window.addEventListener('pointercancel', onPointerCancel, true)

    return () => {
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('pointercancel', onPointerCancel, true)
    }
  }, [cancelDrag, finishDrop, updateFromClient])

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  const updateBackgroundTint = useCallback(
    (slot: BackgroundDropSlot, tint: number, persist: boolean) => {
      const targetSpreadId = spreadIdRef.current

      if (!targetSpreadId) return

      const current = savedBySpreadRef.current[targetSpreadId] ?? EMPTY_SAVED
      const ref = current[slot]

      if (!ref) return

      const next = withSavedBackgroundTint(current, slot, tint)

      if (!persist) {
        if (next === current) return
        if (!(targetSpreadId in tintRollbackRef.current)) {
          tintRollbackRef.current[targetSpreadId] = current
        }

        savedBySpreadRef.current = { ...savedBySpreadRef.current, [targetSpreadId]: next }
        setSavedBySpread((prev) => ({ ...prev, [targetSpreadId]: next }))
        return
      }

      const rollback = tintRollbackRef.current[targetSpreadId] ?? current

      delete tintRollbackRef.current[targetSpreadId]

      // Nothing changed since the last save (e.g. a slider click that didn't move the thumb).
      if (next === current && rollback === current) return

      const request = buildSavedBackgroundTintRequest(ref, slot, tint)

      if (!request || !isCheckpointLoaded) {
        setSavedBySpread((prev) => ({ ...prev, [targetSpreadId]: rollback }))
        toast.error("Couldn't change the tint. Please try again.")
        return
      }

      runBackgroundWrite({
        targetSpreadId,
        optimistic: next,
        rollback,
        writes: [() => checkpointBackgroundsService.updateBackground(targetSpreadId, request)],
        errorMessage: "Couldn't change the tint. Please try again.",
        // From the tint before the gesture's unsaved previews, so one undo reverts the whole drag.
        onRolledBack: recordChange(targetSpreadId, rollback, next),
      })
    },
    [isCheckpointLoaded, recordChange, runBackgroundWrite]
  )

  const removeBackground = useCallback(
    (slot: BackgroundDropSlot) => {
      const targetSpreadId = spreadIdRef.current

      if (!targetSpreadId) return

      const current = savedBySpreadRef.current[targetSpreadId] ?? EMPTY_SAVED

      if (!current[slot]) return

      if (!isCheckpointLoaded) {
        toast.error('The spread is still loading. Try removing the background again.')
        return
      }

      const rollback = tintRollbackRef.current[targetSpreadId] ?? current

      delete tintRollbackRef.current[targetSpreadId]

      const optimistic = clearBackgroundPlacement(current, slot)

      runBackgroundWrite({
        targetSpreadId,
        optimistic,
        rollback,
        writes: [() => checkpointBackgroundsService.deleteBackground(targetSpreadId, slot)],
        errorMessage: "Couldn't remove the background. Please try again.",
        onRolledBack: recordChange(targetSpreadId, rollback, optimistic),
      })
    },
    [isCheckpointLoaded, recordChange, runBackgroundWrite]
  )

  /**
   * Undo/redo replay: moves `targetSpreadId`'s backgrounds to `slots` and saves the difference
   * (deletes, then PUTs). Records no history — the history step itself is the record. History only
   * replays steps for the spread on screen: home's undo history skips a step recorded on another
   * spread (`isBackgroundStepForOtherSpread`, the `shouldSkip` of its `useUndoRedo`) rather than
   * writing to a checkpoint the user has exited. When the restore doesn't take effect, `onFailed`
   * says whether it's worth retrying: home puts a retryable step back on its stack and drops a
   * permanent one (`settleFailedBackgroundRestore`).
   */
  const restoreSpreadBackgrounds = useCallback<RestoreSpreadBackgrounds>(
    (targetSpreadId, slots, onFailed) => {
      if (!targetSpreadId) return

      if (targetSpreadId === spreadIdRef.current && !isCheckpointLoaded) {
        toast.error("The spread is still loading, so the background change couldn't be undone.")
        // Loading finishes on its own — worth trying the same step again.
        onFailed?.({ retryable: true })
        return
      }

      const current = savedBySpreadRef.current[targetSpreadId] ?? EMPTY_SAVED
      const plan = planSpreadBackgroundsRestore(current, slots)

      if (plan.skippedSlots.length > 0) {
        logger.warn('Background history restore skipped unsaveable slots', plan.skippedSlots)
      }

      const writes = [
        ...plan.deleteSlots.map(
          (slot) => () => checkpointBackgroundsService.deleteBackground(targetSpreadId, slot)
        ),
        ...plan.putRequests.map(
          (request) => () => checkpointBackgroundsService.updateBackground(targetSpreadId, request)
        ),
      ]

      if (writes.length === 0) {
        // Every difference was unsaveable (a Solid ref with no colorId): nothing changes, and
        // never will — retrying can't help, so the step is dropped rather than retried forever.
        if (plan.skippedSlots.length > 0) {
          toast.error(BACKGROUND_UNDO_IMPOSSIBLE_MESSAGE)
          onFailed?.({ retryable: false })
        }

        return
      }

      delete tintRollbackRef.current[targetSpreadId]

      runBackgroundWrite({
        targetSpreadId,
        optimistic: slots,
        rollback: current,
        writes,
        // A retryable failure keeps its step, so "try again" is true; a permanent one drops it,
        // and a second Undo would undo something else — so say it can't be undone instead.
        errorMessage: (error) =>
          isRetryableBackgroundWriteError(error)
            ? "Couldn't restore the background. Please try again."
            : BACKGROUND_UNDO_IMPOSSIBLE_MESSAGE,
        onRolledBack: onFailed
          ? (error) => onFailed({ retryable: isRetryableBackgroundWriteError(error) })
          : undefined,
      })
    },
    [isCheckpointLoaded, runBackgroundWrite]
  )

  useEffect(() => {
    if (!restoreSpreadBackgroundsRef) return

    restoreSpreadBackgroundsRef.current = restoreSpreadBackgrounds

    return () => {
      if (restoreSpreadBackgroundsRef.current === restoreSpreadBackgrounds) {
        restoreSpreadBackgroundsRef.current = null
      }
    }
  }, [restoreSpreadBackgroundsRef, restoreSpreadBackgrounds])

  useEffect(() => {
    if (!flushSpreadBackgroundWritesRef) return

    flushSpreadBackgroundWritesRef.current = flushSpreadBackgroundWrites

    return () => {
      if (flushSpreadBackgroundWritesRef.current === flushSpreadBackgroundWrites) {
        flushSpreadBackgroundWritesRef.current = null
      }
    }
  }, [flushSpreadBackgroundWritesRef, flushSpreadBackgroundWrites])

  const placedBackgrounds = useMemo(
    () => resolveSavedSpreadBackgrounds(saved, backgroundsById),
    [saved, backgroundsById]
  )

  const actionsValue = useMemo<BackgroundPlacementActions>(
    () => ({
      beginPointerDrag,
      activateViaKeyboard,
      cancelDrag,
      registerCanvasApi,
      isBackgroundsPanelOpen,
      setBackgroundsPanelOpen,
      updateBackgroundTint,
      removeBackground,
    }),
    [
      activateViaKeyboard,
      beginPointerDrag,
      cancelDrag,
      isBackgroundsPanelOpen,
      registerCanvasApi,
      removeBackground,
      updateBackgroundTint,
    ]
  )

  const contextValue = useMemo<BackgroundPlacementContextValue>(
    () => ({
      ...actionsValue,
      isDraggingBackground: phase === 'dragging',
      draggingBackgroundId: background?.backgroundId ?? null,
      draggingBackground: background,
      hoverSlot,
      placedBackgrounds,
    }),
    [actionsValue, background, hoverSlot, phase, placedBackgrounds]
  )

  const showGhost = phase === 'dragging' && background != null && thumbRect != null

  return (
    <BackgroundPlacementActionsContext.Provider value={actionsValue}>
      <BackgroundPlacementContext.Provider value={contextValue}>
        {children}
        {showGhost &&
          background != null &&
          createPortal(
            <BackgroundDragGhost
              ref={ghostElRef}
              background={background}
              initialClientX={ghostClient.x}
              initialClientY={ghostClient.y}
              overTarget={hoverSlot != null}
            />,
            document.body
          )}
      </BackgroundPlacementContext.Provider>
    </BackgroundPlacementActionsContext.Provider>
  )
}
